import * as React from "react";
import { Button } from "@byko/component-buttons";
import { useCallback, useEffect, useRef, useState } from "react";
import { ChatBox, ChatContainer, ChatHeader, ChatSelect } from "./style";
import { H2 } from "@byko/component-typography";
import { theme } from "@byko/lib-styles";
import { LongArrowSideIcons, NotandiIcons, WrenchSmallIcons } from "@byko/lib-icons";
import { Input } from "@byko/component-inputs";
import { DotLoader, MainLoader } from "@byko/component-loaders";
import { IconButton } from "@byko/component-buttons";
import {
  ChatFileLabel,
  Deild,
  DepartmentLabelField,
  DepartmentLabelPlaceholder,
  DepartmentLabelTitle,
  DepartmentModalBodyStyle,
  DepartmentSelectStyle,
  DisconnectConfirmBox,
  DisconnectConfirmButtonRow,
  DisconnectConfirmText,
  DisconnectConfirmWrapper,
  LoadingWrapper,
  Text,
  attachmentStyle,
  chatSendButtonStyle,
  h2Style,
  headerButtonGroupStyle,
  headerButtonSpanStyle,
  headerButtonStyle,
  headerButtonStyleNoMargin,
  headerContainerStyle,
} from "./style";
import { AgentAvatar, ChatBubbleAgent, ChatBubbleUser, ChatMessageAgent, ChatMessageUser } from "./style";
import type { Message, WebSocketResponse } from "./types";
import { useChat } from "./use-chat";

const chatInputWrapperStyle: React.CSSProperties = { position: "relative", width: "100%" };
const chatInputStyle: React.CSSProperties = { paddingRight: 88, borderRadius: 0 };
const chatSendButtonWrapperStyle: React.CSSProperties = {
  position: "absolute",
  right: 0,
  top: 0,
  height: 44,
  display: "flex",
  alignItems: "center",
};

const Header = ({ label, onClose, onMinimize }: { label: string; onMinimize: () => void; onClose: () => void }) => (
  <div style={headerContainerStyle}>
    <H2 maxFontSize="24px" style={h2Style} textColor={theme.palette.yellow.dark}>
      {label || "Netspjall"}
    </H2>
    <div style={headerButtonGroupStyle}>
      <button aria-label="Minimize chat" style={headerButtonStyle} type="button" onClick={onMinimize}>
        <span style={headerButtonSpanStyle}>_</span>
      </button>
      <button aria-label="Close chat" style={headerButtonStyleNoMargin} type="button" onClick={onClose}>
        <span style={headerButtonSpanStyle}>×</span>
      </button>
    </div>
  </div>
);

const DisconnectConfirm = ({ onConfirm, onCancel }: { onConfirm: () => void; onCancel: () => void }) => (
  <DisconnectConfirmWrapper>
    <DisconnectConfirmBox>
      <DisconnectConfirmText>Ertu viss um að þú viljir loka netspjalli?</DisconnectConfirmText>
      <DisconnectConfirmButtonRow>
        <Button aria-label="Hætta við" buttonColor="whiteButton" label="Hætta við" onClick={onCancel} />
        <Button aria-label="Loka netspjalli" buttonColor="yellowButton" label="Loka netspjalli" onClick={onConfirm} />
      </DisconnectConfirmButtonRow>
    </DisconnectConfirmBox>
  </DisconnectConfirmWrapper>
);

export const ChatWidget = (): JSX.Element => {
  const {
    state,
    setWaitingForAgent,
    setShowDepartmentSelect,
    setSelectedDepartment,
    setOpen,
    setShowDisconnectConfirm,
    setBlockedFileTypes,
    setMaxFileSizeKB,
    setAllowedFileTypes,
    setFileError,
  } = useChat();

  const tokenRef = useRef<string>("");
  const [currentToken, setCurrentToken] = useState<string>("");
  const [currentFile, setCurrentFile] = useState<File | null>(null);
  const [currentAttachmentId, setCurrentAttachmentId] = useState<string | null>(null);
  const [messageInput, setMessageInput] = useState<string>("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const [agentName, setAgentName] = useState<string | null>(null);
  const chatBoxRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState<string>("");
  const currentFileUploadRef = useRef<File | null>(null);
  const hasTriedReconnectRef = React.useRef(false);
  const currentFileRef = useRef<File | null>(null);
  const currentAttachmentIdRef = useRef<string | null>(null);

  const addMessage = useCallback((messageObj: Message) => {
    setMessages((prevMessages) => [
      ...prevMessages,
      {
        ...messageObj,
        timestamp: messageObj.timestamp || Date.now(),
      },
    ]);
  }, []);

  const handleDisconnect = useCallback(() => {
    if (state.socketRef.current && state.socketRef.current.readyState === WebSocket.OPEN) {
      addMessage({ type: "system", text: "Spjallinu hefur verið lokað" });
      const closeMessage = {
        action: "closeSession",
        token: tokenRef.current,
        closeAllConnections: true,
      };
      setWaitingForAgent(false);
      localStorage.removeItem(state.CHAT_TOKEN_KEY);
      localStorage.removeItem(state.CHAT_DEPT_KEY);
      tokenRef.current = "";
      state.socketRef.current.send(JSON.stringify(closeMessage));
      state.socketRef.current.close();
    }
  }, [addMessage]);

  const disconnectChat = useCallback(() => {
    setShowDisconnectConfirm(true);
  }, []);

  const handleDisconnectConfirm = useCallback(() => {
    setShowDisconnectConfirm(false);
    handleDisconnect();
    setShowDepartmentSelect(false);
    setOpen(false);
    setSelectedDepartment(null);
    setMessages([]);
    setCurrentToken("");
    setCurrentFile(null);
    setCurrentAttachmentId(null);
    setMessageInput("");
    setIsTyping(false);
    setAgentName(null);
    setName("");
    setWaitingForAgent(false);
    localStorage.removeItem(state.CHAT_TOKEN_KEY);
    localStorage.removeItem(state.CHAT_DEPT_KEY);
    tokenRef.current = "";
  }, [handleDisconnect]);

  const handleDisconnectCancel = useCallback(() => {
    setShowDisconnectConfirm(false);
  }, []);

  useEffect(() => {
    currentFileRef.current = currentFile;
  }, [currentFile]);
  useEffect(() => {
    currentAttachmentIdRef.current = currentAttachmentId;
  }, [currentAttachmentId]);

  const sendMessageWithAttachment = useCallback(
    (attachmentId: string, file: File | null) => {
      if (!state.socketRef.current || !attachmentId || !file) return;
      const message = {
        action: "onMessage",
        token: tokenRef.current,
        message: {
          type: "Text",
          text: messageInput || "Skrá send",
          content: [
            {
              contentType: "Attachment",
              attachment: { id: attachmentId },
            },
          ],
        },
      };
      state.socketRef.current.send(JSON.stringify(message));
      addMessage({
        type: "human",
        nickname: name,
        text: messageInput,
        attachments: [{ filename: file.name }],
        timestamp: Date.now(),
      });
      setCurrentFile(null);
      setCurrentAttachmentId(null);
      setMessageInput("");
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    [messageInput, addMessage, name],
  );

  useEffect(() => {
    currentFileUploadRef.current = currentFile;
  }, [currentFile]);

  const uploadFile = useCallback(async (url: string, headers: Record<string, string>, _attachmentId: string) => {
    if (!currentFileUploadRef.current) return;
    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: headers,
        body: currentFileUploadRef.current,
      });
      if (!response.ok) {
        throw new Error("Upload failed");
      }
    } catch (error) {
      console.error("Villa kom upp við að hlaða upp skrá. Vinsamlegast reyndu aftur.");
    }
  }, []);

  const connect = useCallback(
    (departmentArg?: string) => {
      const dept = departmentArg || state.selectedDepartment;
      if (!dept) return;
      let token = tokenRef.current;

      if (!token) {
        token = crypto.randomUUID();
        setCurrentToken(token);
        tokenRef.current = token;
      }

      // Persist session
      localStorage.setItem(state.CHAT_TOKEN_KEY, token);
      localStorage.setItem(state.CHAT_DEPT_KEY, dept);
      const wsUrl = state.endpoint;
      const deploymentId = state.deploymentIds[dept];
      const newSocket = new WebSocket(`${wsUrl}?deploymentId=${deploymentId}&token=${token}`);
      state.socketRef.current = newSocket;

      newSocket.onopen = () => {
        hasTriedReconnectRef.current = false;
        const configureSessionMessage = {
          action: "configureSession",
          deploymentId,
          token: tokenRef.current,
        };
        newSocket.send(JSON.stringify(configureSessionMessage));
      };

      newSocket.onmessage = (event: MessageEvent) => {
        const response: WebSocketResponse = JSON.parse(event.data);
        // --- System and presence events ---
        if (response.type === "response" && response.class === "GetConfigurationResponse" && response.code === 200) {
          setWaitingForAgent(true);
          if (
            response.body &&
            response.body.allowedMedia &&
            response.body.allowedMedia.inbound &&
            Array.isArray(response.body.allowedMedia.inbound.fileTypes)
          ) {
            setAllowedFileTypes(
              response.body.allowedMedia.inbound.fileTypes
                .map((ft: { type?: string }) => ft.type)
                .filter((t): t is string => !!t),
            );
          }
          const configureSessionMessage = {
            action: "configureSession",
            deploymentId,
            token: tokenRef.current,
          };
          newSocket.send(JSON.stringify(configureSessionMessage));
        } else if (response.type === "response" && response.class === "SessionResponse" && response.code === 200) {
          // Extract blocked file types and max file size
          if (response.body) {
            if (Array.isArray(response.body.blockedExtensions)) {
              setBlockedFileTypes(response.body.blockedExtensions);
            }
            if (
              response.body.allowedMedia &&
              response.body.allowedMedia.inbound &&
              typeof response.body.allowedMedia.inbound.maxFileSizeKB === "number"
            ) {
              setMaxFileSizeKB(response.body.allowedMedia.inbound.maxFileSizeKB);
            }
          }
          const joinMessage = {
            action: "onMessage",
            token: tokenRef.current,
            message: {
              type: "Event",
              events: [{ eventType: "Presence", presence: { type: "Join" } }],
            },
          };
          newSocket.send(JSON.stringify(joinMessage));
        } else if (
          response.type === "message" &&
          response.body &&
          response.body.type === "Event" &&
          Array.isArray(response.body.events) &&
          response.body.events[0] &&
          response.body.events[0].eventType === "Presence" &&
          response.body.events[0].presence
        ) {
          const presenceEvent = response.body.events[0].presence;
          const isOutbound = response.body?.direction === "Outbound";
          if (presenceEvent) {
            switch (presenceEvent.type) {
              case "Join": {
                if (
                  isOutbound ||
                  (response.body?.channel && response.body?.channel.from && response.body?.channel.from.nickname)
                ) {
                  setWaitingForAgent(false); // Agent has joined, stop waiting
                  const agent = response.body?.channel?.from?.nickname || "Þjónustufulltrú";
                  setAgentName(agent); // <-- set agent name on join
                  addMessage({ type: "system", text: `${agent} hefur tengst spjallinu` });
                }
                break;
              }
              case "Leave":
              case "Disconnect": {
                if (isOutbound) {
                  const agent = response.body?.channel?.from?.nickname || "Þjónustufulltrú";
                  setAgentName(null); // <-- clear agent name on leave/disconnect
                  addMessage({ type: "system", text: `${agent} hefur aftengst spjallinu` });
                  handleDisconnect();
                }
                break;
              }
              case "Transfer":
                const targetAgent = presenceEvent.targetAgent || "nýjum þjónustufulltrúa";
                addMessage({ type: "system", text: `Spjallið er að færast til ${targetAgent}` });
                break;
            }
          }
        } else if (
          response.type === "message" &&
          response.body &&
          response.body.type === "Event" &&
          Array.isArray(response.body.events) &&
          response.body.events[0] &&
          response.body.events[0].eventType === "SessionEnd"
        ) {
          addMessage({ type: "system", text: "Spjallinu hefur verið lokað" });
          setTimeout(() => {
            disconnectChat();
          }, 3000);
        } else if (response.type === "message" && response.body && response.body.direction === "Outbound") {
          // --- Agent/Bot messages ---
          if (
            response.type === "message" &&
            response.body &&
            response.body.type === "Event" &&
            Array.isArray(response.body.events) &&
            response.body.events[0] &&
            response.body.events[0].eventType === "Typing" &&
            response.body.events[0].typing
          ) {
            const typingEvent = response.body.events[0].typing;
            if (response.body.direction === "Outbound" && typingEvent) {
              setIsTyping(typingEvent.type === "On");
              if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
              }

              typingTimeoutRef.current = window.setTimeout(() => {
                setIsTyping(false);
              }, 5000);
            }
          } else if (response.body.text || (response.body.content && response.body.content.length > 0)) {
            if (typingTimeoutRef.current) {
              clearTimeout(typingTimeoutRef.current);
            }
            setIsTyping(false);
            const isBot = response.body.originatingEntity === "Bot";

            if (isBot === false) {
              setWaitingForAgent(false); // Agent has sent a message, stop waiting
            }

            const messageObj: Message = {
              type: isBot ? "bot" : "agent",
              text: response.body.text || "",
              attachments: [],
            };
            if (!isBot && response.body.channel && response.body.channel.from && response.body.channel.from.nickname) {
              messageObj.nickname = response.body.channel.from.nickname;
            }
            if (response.body.content && response.body.content.length > 0) {
              messageObj.attachments = [];
              response.body.content.forEach((content) => {
                if (content.contentType === "Attachment" && content.attachment) {
                  messageObj.attachments?.push({
                    url: content.attachment.url || "",
                    filename: content.attachment.filename || "Viðhengi",
                  });
                }
              });
            }
            addMessage(messageObj);
          }
        } else if (response.type === "response" && response.class === "PresignedUrlResponse" && response.code === 200) {
          if (response.body?.url && response.body?.headers && response.body?.attachmentId) {
            uploadFile(response.body.url, response.body.headers, response.body.attachmentId);
          }
        } else if (response.type === "message" && response.class === "UploadSuccessEvent") {
          if (response.body?.attachmentId) {
            setCurrentAttachmentId(response.body.attachmentId);
            sendMessageWithAttachment(response.body.attachmentId, currentFileRef.current);
          }
        }
      };

      newSocket.onclose = (event) => {
        console.log("Connection closed:", event);
      };

      newSocket.onerror = (error) => {
        console.error("WebSocket error:", error);
        // if (!hasTriedReconnectRef.current) {
        //   hasTriedReconnectRef.current = true;
        //   setTimeout(() => {
        //     connect(dept); // Try reconnecting once with same tokenRef.current
        //   }, 2000);
        // }
      };
    },
    [
      state.selectedDepartment,
      state.endpoint,
      state.deploymentIds,
      addMessage,
      disconnectChat,
      handleDisconnect,
      sendMessageWithAttachment,
      uploadFile,
      setAgentName,
    ],
  );

  const handleShowDepartmentSelect = useCallback(() => {
    // If there is an active session, just open the chat
    if (currentToken && state.selectedDepartment) {
      setOpen(true);
      setShowDepartmentSelect(false);
    } else {
      setShowDepartmentSelect(true);
      setOpen(false);
    }
  }, [currentToken, state.selectedDepartment]);

  // Update handleDepartmentSelect to open chat and hide department selection
  const handleDepartmentSelect = useCallback(
    (department: string) => {
      setSelectedDepartment(department);
      setName(name);
      setShowDepartmentSelect(false);
      setOpen(true);
      connect(department); // Only pass department, tokenArg is optional
    },
    [name, connect],
  );

  // Memoize department select handlers
  const handleDepartmentSelectThjonustuver = useCallback(
    () => handleDepartmentSelect("Þjónustuver"),
    [handleDepartmentSelect],
  );
  const handleDepartmentSelectTaekjaleiga = useCallback(
    () => handleDepartmentSelect("Tækjaleiga"),
    [handleDepartmentSelect],
  );

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = useCallback(() => {
    const message = messageInput.trim();
    if (message && state.socketRef.current && state.socketRef.current.readyState === WebSocket.OPEN) {
      const payload = {
        action: "onMessage",
        token: tokenRef.current,
        message: {
          type: "Text",
          text: message,
          channel: {
            metadata: {
              customAttributes: {
                name: name || "No name",
              },
            },
          },
        },
      };

      state.socketRef.current.send(JSON.stringify(payload));
      addMessage({ type: "human", nickname: name || "Þú", text: message, timestamp: Date.now() });
      setMessageInput("");
    }
  }, [messageInput, addMessage, name]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        event.preventDefault();
        sendMessage();
      }
    },
    [sendMessage],
  );

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setMessageInput(e.target.value);
  }, []);

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files && event.target.files[0];
      setFileError("");
      if (!file) return;

      // Validation
      const ext = file.name.split(".").pop()?.toLowerCase() || "";
      const mime = file.type;
      const isBlocked = state.blockedFileTypes.some((blocked) =>
        blocked.startsWith(".") ? ext === blocked.replace(".", "").toLowerCase() : ext === blocked.toLowerCase(),
      );
      const isAllowed = state.allowedFileTypes.length === 0 || state.allowedFileTypes.includes(mime);
      const isTooLarge = state.maxFileSizeKB !== null && file.size > state.maxFileSizeKB * 1024;

      if (isBlocked) {
        setFileError("Þessi skráartegund er ekki leyfð.");
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
      if (!isAllowed) {
        setFileError("Þessi skráartegund er ekki studd.");
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
      if (isTooLarge) {
        setFileError(`Skráin er of stór. Hámarksstærð er ${state.maxFileSizeKB} KB.`);
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }

      setCurrentFile(file);
      setFileError("");

      try {
        const presignedUrlRequest = {
          action: "onAttachment",
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          token: currentToken,
        };

        if (state.socketRef.current && state.socketRef.current.readyState === WebSocket.OPEN) {
          await state.socketRef.current.send(JSON.stringify(presignedUrlRequest));
        }
      } catch (error) {
        setFileError("Villa kom upp við að meðhöndla skrána. Vinsamlegast reyndu aftur.");
      }
    },
    [
      state.blockedFileTypes,
      state.allowedFileTypes,
      state.maxFileSizeKB,
      currentToken,
      state.socketRef,
      setCurrentFile,
      setFileError,
      fileInputRef,
    ],
  );

  const handleMinimizeDepartmentSelect = useCallback(() => {
    setShowDepartmentSelect(false);
  }, []);
  const handleMinimizeChat = useCallback(() => {
    setOpen(false);
  }, []);

  // Add name state and handleNameChange
  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  }, []);

  // Render messages
  const renderMessages = () =>
    messages.map((msg, idx) => {
      const key = msg.timestamp ? msg.timestamp : idx;
      if (msg.type === "agent" || msg.type === "bot" || msg.type === "system") {
        return (
          <ChatMessageAgent key={key}>
            <AgentAvatar as="span">
              {msg.type === "system" ? (
                <WrenchSmallIcons iconColor={theme.palette.yellow.main} size={8} />
              ) : (
                <NotandiIcons iconColor={theme.palette.yellow.main} size={8} />
              )}
            </AgentAvatar>
            <ChatBubbleAgent>
              {msg.text && <div>{msg.text}</div>}
              {msg.attachments &&
                msg.attachments.map((attachment) => (
                  <div key={attachment.filename} style={attachmentStyle}>
                    {attachment.url ? (
                      <a href={attachment.url} rel="noopener noreferrer" target="_blank">
                        📎 {attachment.filename}
                      </a>
                    ) : (
                      <span>📎 {attachment.filename}</span>
                    )}
                  </div>
                ))}
            </ChatBubbleAgent>
          </ChatMessageAgent>
        );
      }
      // Human/user message
      return (
        <ChatMessageUser key={key}>
          <ChatBubbleUser>
            {msg.text && <div>{msg.text}</div>}
            {msg.attachments &&
              msg.attachments.map((attachment) => (
                <div key={attachment.filename} style={attachmentStyle}>
                  {attachment.url ? (
                    <a href={attachment.url} rel="noopener noreferrer" target="_blank">
                      📎 {attachment.filename}
                    </a>
                  ) : (
                    <span>📎 {attachment.filename}</span>
                  )}
                </div>
              ))}
          </ChatBubbleUser>
        </ChatMessageUser>
      );
    });

  // On mount, check for existing session and resume if found
  useEffect(() => {
    const savedToken = localStorage.getItem(state.CHAT_TOKEN_KEY);
    const savedDept = localStorage.getItem(state.CHAT_DEPT_KEY);
    if (savedToken && savedDept) {
      setCurrentToken(savedToken);
      tokenRef.current = savedToken;
      setSelectedDepartment(savedDept);
      setOpen(true);
      addMessage({ type: "agent", text: "Þú hefur endurtengst netspjalli", timestamp: Date.now() });
      setWaitingForAgent(false);
      connect(savedDept);
    }
  }, []);

  return (
    <>
      {!state.open && state.showDepartmentSelect && (
        <DepartmentSelectStyle>
          <ChatHeader>
            <Header
              label={agentName || "Netspjall"}
              onClose={disconnectChat}
              onMinimize={handleMinimizeDepartmentSelect}
            />
          </ChatHeader>
          <DepartmentModalBodyStyle>
            <Input
              aria-label="Nafn"
              label="Nafn"
              maxLength={20}
              placeholder="Nafn"
              stretch={true}
              type="text"
              value={name}
              onChange={handleNameChange}
            />
            <Deild>
              <DepartmentLabelTitle>Deild</DepartmentLabelTitle>
              <DepartmentLabelField>
                <DepartmentLabelPlaceholder>Almenn fyrirspurn</DepartmentLabelPlaceholder>
              </DepartmentLabelField>
              <Button
                buttonColor="whiteButton"
                customClassName="mobile-login-button"
                icon={LongArrowSideIcons}
                label="Velja"
                stretch={true}
                onClick={handleDepartmentSelectThjonustuver}
              />
            </Deild>
            <Deild>
              <DepartmentLabelField>
                <DepartmentLabelPlaceholder>BYKO leiga</DepartmentLabelPlaceholder>
              </DepartmentLabelField>
              <Button
                buttonColor="whiteButton"
                customClassName="mobile-login-button"
                icon={LongArrowSideIcons}
                label="Velja"
                stretch={true}
                onClick={handleDepartmentSelectTaekjaleiga}
              />
            </Deild>
          </DepartmentModalBodyStyle>
        </DepartmentSelectStyle>
      )}
      {/* Show chat bubble if chat is not open and department selection is not shown */}
      {!state.open && !state.showDepartmentSelect && (
        <button id="chat-button" title="netspjall" type="button" onClick={handleShowDepartmentSelect}>
          <svg height="60px" version="1.1" viewBox="0 0 60 60" width="60px" xmlns="http://www.w3.org/2000/svg">
            <g fill="none" fillRule="evenodd" id="Welcome" stroke="none" strokeWidth="1">
              <g id="Mobile-Front" transform="translate(-295.000000, -1020.000000)">
                <g id="Contact" transform="translate(295.000000, 1020.000000)">
                  <rect fill="#ffd900" height="60" id="Rectangle-2-Copy" rx="30" width="60" x="0" y="0"></rect>
                  <g
                    fill="#FFFFFF"
                    fillRule="nonzero"
                    id="ic_chat_bubble_black_24px"
                    transform="translate(20.000000, 20.000000)"
                  >
                    <path
                      // eslint-disable-next-line max-len
                      d="M18,0 L2,0 C0.9,0 0,0.9 0,2 L0,20 L4,16 L18,16 C19.1,16 20,15.1 20,14 L20,2 C20,0.9 19.1,0 18,0 Z"
                      id="Shape"
                    ></path>
                  </g>
                </g>
              </g>
            </g>
          </svg>
        </button>
      )}
      {state.open && (
        <ChatContainer>
          <ChatHeader>
            <Header label={agentName || "Netspjall"} onClose={disconnectChat} onMinimize={handleMinimizeChat} />
          </ChatHeader>
          <ChatBox ref={chatBoxRef}>
            {state.waitingForAgent && (
              <>
                <Text>Tengist fulltrúa</Text>
                <LoadingWrapper>
                  <MainLoader size={84} />
                </LoadingWrapper>
              </>
            )}
            {renderMessages()}
            {isTyping && <DotLoader size={84} />}
          </ChatBox>
          <hr></hr>
          <ChatSelect>
            <div style={chatInputWrapperStyle}>
              <Input
                aria-label="Skrifaðu skilaboð"
                error={"true"}
                maxLength={500}
                placeholder="Skrifaðu skilaboð"
                stretch={true}
                style={chatInputStyle}
                type="text"
                value={messageInput}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
              />
              <ChatFileLabel htmlFor="chat-file-input">
                <svg fill="none" height="20" viewBox="0 0 24 24" width="20" xmlns="http://www.w3.org/2000/svg">
                  <path
                    // eslint-disable-next-line max-len
                    d="M16.5 13.5V17.25C16.5 18.4926 15.4926 19.5 14.25 19.5H7.5C6.25736 19.5 5.25 18.4926 5.25 17.25V9.75C5.25 8.50736 6.25736 7.5 7.5 7.5H11.25"
                    stroke={theme.palette.blue.main}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M17.25 6.75L19.5 9M19.5 9L17.25 11.25M19.5 9H12.75"
                    stroke={theme.palette.blue.main}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                  />
                </svg>
                <input
                  ref={fileInputRef}
                  id="chat-file-input"
                  style={fileInputHiddenStyle}
                  type="file"
                  onChange={handleFileSelect}
                />
              </ChatFileLabel>
            </div>
            <div style={chatSendButtonWrapperStyle}></div>
            <IconButton
              ariaLabel="Senda"
              buttonColor="yellowButton"
              icon={LongArrowSideIcons}
              small={true}
              style={chatSendButtonStyle}
              onClick={sendMessage}
            />
          </ChatSelect>
          {state.showDisconnectConfirm && (
            <DisconnectConfirm onCancel={handleDisconnectCancel} onConfirm={handleDisconnectConfirm} />
          )}
          {state.fileError && <div style={fileErrorStyle}>{state.fileError}</div>}
        </ChatContainer>
      )}
    </>
  );
};

const fileInputHiddenStyle: React.CSSProperties = { display: "none" };
const fileErrorStyle: React.CSSProperties = { color: "red", marginTop: 8, marginBottom: 8, textAlign: "center" };
